import React, { useState } from "react";
import { Button } from "./ui/button";
import { Loader2, Zap, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api from "../api/client";

/**
 * AddonUpsellInline
 * =================
 * Single-button component that subscribes a tenant to a catalog add-on with
 * zero navigation. Reused by:
 *
 *  - `MicrositesDashboard` → shown when a microsite-create call returns the
 *    new `addon_hint: {addon_slug: "extra_microsite", monthly_price: 9, …}`
 *    response, letting the tenant self-serve +1 microsite without upgrading.
 *  - `FeatureUpgradeModal` → shown on the `white_label` feature gate with the
 *    `white_label` addon so Pro tenants can unlock branding without jumping
 *    to Enterprise.
 *
 * On click: POSTs `/api/addons/subscribe` and redirects to Stripe Checkout.
 */
export default function AddonUpsellInline({
  addonSlug,
  addonName,
  monthlyPrice,
  quantity = 1,
  description,
  cta,              // Optional override for button label
  className = "",
  size = "default",
  "data-testid": testId,
}) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const r = await api.post("/api/addons/subscribe", {
        addon_slug: addonSlug,
        quantity,
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Checkout unavailable");
        setLoading(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscribe failed");
      setLoading(false);
    }
  };

  const label = cta || `Add ${addonName} for $${monthlyPrice}/mo${quantity > 1 ? ` × ${quantity}` : ""}`;

  return (
    <div className={`rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-4 ${className}`} data-testid={testId || `addon-upsell-${addonSlug}`}>
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
          <Zap className="h-4 w-4 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-slate-900">
              {addonName}
            </h4>
            <span className="text-xs font-semibold text-indigo-600">
              ${monthlyPrice}/mo{quantity > 1 ? ` × ${quantity}` : ""}
            </span>
          </div>
          {description && (
            <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{description}</p>
          )}
          <Button
            onClick={handleClick}
            disabled={loading}
            size={size === "sm" ? "sm" : "default"}
            className="mt-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 text-white"
            data-testid={`${testId || `addon-upsell-${addonSlug}`}-cta`}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                {label}
                <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
